import React, { useState } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomPagination from "../pagination";
import { formatDateTime } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "1px solid var(--border-color)",
    color: theme.palette.common.black,
    padding: "12px",
    textAlign: "start",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "14px",
    textAlign: "start",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const statusColor = (status) => {
  if (status === "resolved") return "success";
  if (status === "reprocessing") return "warning";
  return "error";
};

const formatDate = (d) => formatDateTime(d, "—");

const KafkaDeadLetterList = ({
  data,
  currentPage,
  itemsPerPage,
  setPage,
  setRowsPerPage,
  dataCount,
  onRetrigger,
  onResolve,
  loadingId,
}) => {
  const [detailItem, setDetailItem] = useState(null);

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>#</StyledTableCell>
              <StyledTableCell>Topic</StyledTableCell>
              <StyledTableCell>Group ID</StyledTableCell>
              <StyledTableCell>Error</StyledTableCell>
              <StyledTableCell>Fail Count</StyledTableCell>
              <StyledTableCell>Retrigger Count</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Last Failed</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && (
              <TableRow>
                <StyledTableCell colSpan={9} sx={{ textAlign: "center" }}>
                  No dead letter records found.
                </StyledTableCell>
              </TableRow>
            )}
            {data.map((item, index) => (
              <StyledTableRow key={item._id || index}>
                <StyledTableCell>
                  {currentPage * itemsPerPage + index + 1}
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={12}
                    fontWeight={500}
                    color="var(--text-tertiary)"
                  >
                    {item.topic}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={11}
                    color="var(--text-tertiary)"
                  >
                    {item.groupId}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip title={item.errorStack || item.error || ""} arrow>
                    <Typography
                      fontFamily="Poppins"
                      fontSize={11}
                      color="error"
                      noWrap
                      sx={{ maxWidth: 240 }}
                    >
                      {item.error || "—"}
                    </Typography>
                  </Tooltip>
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={item.errorCount || 1}
                    size="small"
                    color={item.errorCount > 1 ? "error" : "default"}
                  />
                </StyledTableCell>
                <StyledTableCell>{item.retriggerCount || 0}</StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={item.status}
                    size="small"
                    color={statusColor(item.status)}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Typography fontFamily="Poppins" fontSize={11}>
                    {formatDate(item.lastFailedAt)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View payload / error details" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setDetailItem(item)}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {item.status !== "resolved" && (
                      <>
                        <Tooltip
                          title="Retrigger — republish to Kafka topic"
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={
                                loadingId === item._id ||
                                item.status === "reprocessing"
                              }
                              onClick={() => onRetrigger(item._id)}
                            >
                              {loadingId === item._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mark as resolved" arrow>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={loadingId === item._id}
                              onClick={() => onResolve(item._id)}
                            >
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        dataCount={dataCount}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
      />

      {/* Detail dialog */}
      <Dialog
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dead Letter Detail — {detailItem?.topic}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Offset: {detailItem?.partition} / {detailItem?.offset} &nbsp;|&nbsp;
            Key: {detailItem?.messageKey || "—"} &nbsp;|&nbsp; First failed:{" "}
            {formatDate(detailItem?.firstFailedAt)}
          </Typography>
          <Typography
            variant="subtitle2"
            color="error"
            gutterBottom
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            Error: {detailItem?.error}
          </Typography>
          {detailItem?.errorStack && (
            <Box
              component="pre"
              sx={{
                fontSize: 11,
                backgroundColor: "var(--row-bg-odd)",
                border: "1px solid var(--border-color)",
                borderRadius: 1,
                p: 1,
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                mb: 2,
              }}
            >
              {detailItem.errorStack}
            </Box>
          )}
          <Typography variant="subtitle2" gutterBottom>
            Message Payload:
          </Typography>
          <Box
            component="pre"
            sx={{
              fontSize: 11,
              backgroundColor: "var(--row-bg-odd)",
              border: "1px solid var(--border-color)",
              borderRadius: 1,
              p: 1,
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {detailItem?.payload
              ? JSON.stringify(detailItem.payload, null, 2)
              : detailItem?.rawValue || "—"}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KafkaDeadLetterList;
